import { _ } from "../../../core/polyglot/polyglot.js";
import { notBlank } from "../../../core/widgets/forms/validators.js";
import { stacker } from "../../../core/elements/index.js";
import { toNoon } from "../../../core/helpers/date-util.js";
import AccountingDocumentWidget from "../../shared/documentInfrastructure/AccountingDocumentWidget.js";
import ForecastAssumptionsWidget from "../../../core/widgets/ForecastAssumptionsWidget/ForecastAssumptionsWidget.js";
import ForecastTabsWidget from "../../../core/widgets/ForecastTabsWidget/ForecastTabsWidget.js";
import makeReportNotificationButton from "../../reports/periodReport/makeReportNotificationButton.js";
import WysiwygViewerWidget from "../../../core/widgets/WysiwygViewerWidget/WysiwygViewerWidget.js";

const forecastDescriptionTabId = "forecast-description-tab";

/**
 * Widget rendering the core of the ForecastViewer.
 */
export default class ForecastViewerDocumentContent extends AccountingDocumentWidget {
	setup({
		onCellClick,
		onSettingsButtonClick,
		hideDrawer,
		onExportButtonClick,
		onBudgetSaved,
	}) {
		super.setup(...arguments);

		this._onSettingsButtonClick = onSettingsButtonClick;
		this._onBudgetSaved = onBudgetSaved;

		const forecast = this._documentState.getForecast();
		const ruleTemplateSource = this._documentState.getRuleTemplateSource();

		this._forecastTabWidget = new ForecastTabsWidget({
			forecast,
			assumptionsWidget: new ForecastAssumptionsWidget({
				forecast,
				ruleTemplateSource,
				onReportAndCellSelectionRequest:
					this.selectReportAndCell.bind(this),
			}),
			extraTabs: [this._makeDescriptionTab()],
			isInSaveSpaceMode: () => this._documentState.isInSaveSpaceMode(),
			onTabClicked: (currentReport) => {
				hideDrawer();
				this._documentState.setCurrentReport(currentReport);
			},
			actions: this._renderActionsOn.bind(this),
			onCellClick,
		});

		this._onExportButtonClick = onExportButtonClick;

		this.setCurrentTab({ report: this._documentState.getCurrentReport() });
	}

	setCurrentTab({ report }) {
		this._forecastTabWidget.setCurrentTabFromReport(report);
	}

	selectCell(...rest) {
		this._forecastTabWidget.selectCell(...rest);
	}

	selectReportAndCell(...rest) {
		this._forecastTabWidget.selectReportAndCell(...rest);
	}

	scrollToSelectedCell() {
		this._forecastTabWidget.scrollToSelectedCell();
	}

	getCurrentReport() {
		return this._forecastTabWidget.getCurrentReport();
	}

	updateCurrentReport() {
		this._forecastTabWidget.updateCurrentReport();
	}

	updateSaveSpaceMode() {
		this._forecastTabWidget.updateSaveSpaceMode();
	}

	renderContentOn(html) {
		html.div(
			{ class: "ForecastViewerDocumentContent" },
			this._forecastTabWidget,
		);
	}

	_renderActionsOn(html) {
		stacker(
			{ renderOn: html },
			this._renderSettingsButtonOn.bind(this),
			this._mailNotificationRenderer(),
			this._renderExportButtonOn.bind(this),
			this._renderSaveToNewBudgetButtonOn.bind(this),
		);
	}

	_renderSettingsButtonOn(html) {
		html.button(
			{
				class: "btn",
				click: () =>
					this._onSettingsButtonClick({
						report: this.getCurrentReport(),
					}),
				"data-test": "forecast-display-settings",
			},
			_("Display settings"),
		);
	}

	_renderExportButtonOn(html) {
		html.button(
			{
				class: "btn btn-primary",
				click: () => this._onExportButtonClick(),
				"data-test": "forecast-export",
			},
			_("Export"),
		);
	}

	_renderSaveToNewBudgetButtonOn(html) {
		if (!this._documentState.canSaveToBudget()) return;

		html.button(
			{
				class: "btn btn-primary",
				disabled: !this._documentState.canSaveToBudget(),
				click: () => this._showSaveForecastToNewBudgetFormDialog(),
				"data-test": "forecast-save-to-budget",
			},
			_("Convert to new budget"),
		);
	}

	_showSaveForecastToNewBudgetFormDialog() {
		this._configure({
			title: _("Convert to new budget"),
			okLabel: _("Save"),
			formBuilder: (form) => {
				form.input({
					label: _("Budget name"),
					placeHolder: _("Budget name"),
					attribute: "budgetName",
					name: "budgetName",
					validator: notBlank(),
				});

				form.newDropDownField({
					label: _("Fiscal year"),
					attribute: "year",
					name: "year",
					items: this._makeFiscalYearItems(),
				});
			},
			formSpec: {
				saveAction: async ({ budgetName, year }) => {
					const budget = await this._saveForecastToNewBudget({
						budgetName,
						year,
					});

					this._onBudgetSaved(budget);
				},
			},
		});
	}

	_makeFiscalYearItems() {
		const forecast = this._documentState.getForecast();
		const years = forecast.getStartAndEndYearsFromPredictedPeriods();
		return years.map((year) => this._makeFiscalYearItem(year));
	}

	_makeFiscalYearItem(year) {
		const { startYear, endYear } = year;
		const label =
			startYear === endYear ? startYear : `${startYear}-${endYear}`;
		return { label, value: year };
	}

	_saveForecastToNewBudget({ budgetName, year }) {
		const { startYear, startMonth, endYear, endMonth } = year;
		const startDate = new Date(startYear, startMonth - 1, 1);
		const endDate = new Date(endYear, endMonth, 0);

		return this._documentState.saveForecastToNewBudget({
			budgetName,
			start: toNoon(startDate),
			end: toNoon(endDate),
		});
	}

	_mailNotificationRenderer() {
		return makeReportNotificationButton({
			label: _("Notify"),
			cssClass: "btn-default",
			...this._documentState.getNotificationSettings(),
		});
	}

	_makeDescriptionTab() {
		return {
			name: _("Description"),
			id: forecastDescriptionTabId,
			content: this._makeForecastDescriptionViewerRenderer(),
		};
	}

	_makeForecastDescriptionViewerRenderer() {
		return new WysiwygViewerWidget({
			content: this._documentState.getForecastDescription(),
			dataTestName: "forecast-description",
		});
	}
}
